import React, { useState, useEffect } from "react";
import * as material from "@mui/material";
import * as styles from "@mui/material/styles";
import {
  WbSunny,
  Cloud,
  Opacity,
  Thunderstorm,
  AcUnit,
} from "@mui/icons-material";

const theme = styles.createTheme({
  palette: {
    background: { default: "#f5f5f5" },
  },
});

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/* ✅ Weather Icon Mapping for OpenWeather codes */
const WeatherIcon = ({ weatherCode }) => {
  if (!weatherCode) return <Cloud sx={{ color: "gray", fontSize: 36 }} />;

  if (weatherCode >= 200 && weatherCode < 300)
    return <Thunderstorm sx={{ color: "gold", fontSize: 36 }} />; // Thunderstorm
  if (weatherCode >= 300 && weatherCode < 600)
    return <Opacity sx={{ color: "blue", fontSize: 36 }} />; // Rain/Drizzle
  if (weatherCode >= 600 && weatherCode < 700)
    return <AcUnit sx={{ color: "lightblue", fontSize: 36 }} />; // Snow
  if (weatherCode === 800)
    return <WbSunny sx={{ color: "gold", fontSize: 36 }} />; // Clear
  if (weatherCode > 800) return <Cloud sx={{ color: "gray", fontSize: 36 }} />; // Clouds
  return <Cloud sx={{ color: "gray", fontSize: 36 }} />;
};

export default function WeatherApp() {
  const [location, setLocation] = useState({
    city: "",
    region: "",
    country: "",
  });
  const [currentWeather, setCurrentWeather] = useState(null);
  const [weeklyForecast, setWeeklyForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiKey = "9425d3f5a8a01fb31de8543e8cce0c71"; // ✅ your OpenWeather API key

  /* ✅ Unified OpenWeather fetcher (One Call 3.0) */
  const fetchOpenWeatherData = async (lat, lon) => {
    const url = `https://api.openweathermap.org/data/3.0/onecall?lat=${lat}&lon=${lon}&appid=${apiKey}&units=metric`;
    const res = await fetch(url);
    if (!res.ok) throw new Error("Failed to fetch OpenWeather data");
    const data = await res.json();

    const current = {
      values: {
        temperature: data.current.temp,
        humidity: data.current.humidity,
        uvIndex: data.current.uvi,
        windSpeed: data.current.wind_speed, // m/s
        visibility: (data.current.visibility ?? 0) / 1000, // km
        cloudCover: data.current.clouds,
        precipitationProbability: (data.daily?.[0]?.pop ?? 0) * 100,
        weatherCode: data.current.weather?.[0]?.id,
      },
    };

    const daily = (data.daily ?? []).slice(0, 7).map((day) => ({
      time: new Date(day.dt * 1000),
      values: {
        temperatureMax: day.temp.max,
        weatherCodeMax: day.weather?.[0]?.id,
      },
    }));

    return { current, daily };
  };

  /* ✅ Get user location from IP */
  const fetchUserLocation = async () => {
    const ipRes = await fetch(`https://api.ipify.org?format=json`);
    const ipData = await ipRes.json();
    const ip = ipData.ip;

    const locRes = await fetch(`https://ipinfo.io/${ip}?token=113d6fbfe6ec3e`);
    const locData = await locRes.json();
    return {
      city: locData.city,
      region: locData.region,
      country: locData.country,
    };
  };

  /* ✅ Main weather fetch */
  useEffect(() => {
    const getWeatherData = async () => {
      setLoading(true);
      try {
        const userLocation = await fetchUserLocation();
        setLocation(userLocation);

        // Convert city → lat/lon
        const geoRes = await fetch(
          `https://api.openweathermap.org/geo/1.0/direct?q=${userLocation.city}&limit=1&appid=${apiKey}`
        );
        const [geoData] = await geoRes.json();
        if (!geoData) throw new Error("City not found");

        // Fetch weather
        const { current, daily } = await fetchOpenWeatherData(
          geoData.lat,
          geoData.lon
        );

        setCurrentWeather(current);
        setWeeklyForecast(daily);
      } catch (err) {
        console.error("❌ Weather fetch failed:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    getWeatherData();
  }, []);

  /* ✅ Render UI */
  if (loading) return <div style={{ textAlign: "center" }}>Loading...</div>;
  if (error) return <div style={{ color: "red" }}>Error: {error}</div>;

  return (
    <styles.ThemeProvider theme={theme}>
      <material.Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <material.Grid container spacing={3}>
          {/* LEFT PANEL */}
          <material.Grid item xs={12} md={4}>
            <material.Paper sx={{ p: 3, borderRadius: 2 }}>
              <material.Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <WeatherIcon
                  weatherCode={currentWeather?.values?.weatherCode}
                />
                <material.Typography variant="h3" gutterBottom>
                  {Math.round(currentWeather?.values?.temperature)}°C
                </material.Typography>
                <material.Typography color="textSecondary">
                  {currentWeather?.values?.cloudCover}% Cloudy
                </material.Typography>
                <material.Typography color="textSecondary">
                  Rain -{" "}
                  {Math.round(currentWeather?.values?.precipitationProbability)}
                  %
                </material.Typography>
              </material.Box>

              <material.Box
                sx={{
                  mt: 2,
                  p: 1,
                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                  borderRadius: 1,
                  textAlign: "center",
                }}
              >
                <material.Typography variant="body2">
                  {location.city}, {location.region}, {location.country}
                </material.Typography>
              </material.Box>
            </material.Paper>
          </material.Grid>

          {/* RIGHT PANEL */}
          <material.Grid item xs={12} md={8}>
            <material.Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
              <material.Typography variant="h6" gutterBottom>
                Weekly Forecast
              </material.Typography>

              <material.Grid container spacing={2}>
                {weeklyForecast.map((day, index) => {
                  const date = new Date(day.time);
                  const dayName = weekDays[date.getDay()];
                  return (
                    <material.Grid item key={index} xs={6} sm={4} md={2}>
                      <material.Card sx={{ p: 1 }}>
                        <material.CardContent sx={{ textAlign: "center" }}>
                          <material.Typography variant="body2">
                            {dayName}
                          </material.Typography>
                          <WeatherIcon
                            weatherCode={day.values.weatherCodeMax}
                          />
                          <material.Typography variant="body2" sx={{ mt: 1 }}>
                            {Math.round(day.values.temperatureMax)}°
                          </material.Typography>
                        </material.CardContent>
                      </material.Card>
                    </material.Grid>
                  );
                })}
              </material.Grid>

              <material.Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Highlights
              </material.Typography>

              <material.Grid container spacing={2}>
                {[
                  {
                    title: "UV Index",
                    value: currentWeather?.values?.uvIndex ?? "N/A",
                    icon: <WbSunny />,
                  },
                  {
                    title: "Wind Speed",
                    value: `${currentWeather?.values?.windSpeed ?? "N/A"} m/s`,
                    icon: <WbSunny />,
                  },
                  {
                    title: "Humidity",
                    value: `${currentWeather?.values?.humidity ?? "N/A"}%`,
                    icon: <Opacity />,
                  },
                  {
                    title: "Visibility",
                    value: `${currentWeather?.values?.visibility ?? "N/A"} km`,
                    icon: <AcUnit />,
                  },
                ].map((item) => (
                  <material.Grid item xs={12} sm={6} md={3} key={item.title}>
                    <material.Card>
                      <material.CardContent>
                        <material.Typography color="textSecondary" gutterBottom>
                          {item.title}
                        </material.Typography>
                        <material.Typography variant="h5">
                          {item.value}
                        </material.Typography>
                      </material.CardContent>
                    </material.Card>
                  </material.Grid>
                ))}
              </material.Grid>
            </material.Paper>
          </material.Grid>
        </material.Grid>
      </material.Container>
    </styles.ThemeProvider>
  );
}
